import { Button } from "@/components/ui/button";
import { ArrowRight, Globe } from "lucide-react";
import heroImage from "@/assets/hero-consulting.jpg";

interface HeroProps {
  language: 'en' | 'de';
  onRequestQuote: () => void;
}

export const Hero = ({ language, onRequestQuote }: HeroProps) => {
  const content = {
    en: {
      headline: "Strategic Leadership for Digital Transformation",
      subheadline: "Independent Executive Consulting for CIOs, CTOs & CEOs",
      description: "Navigate complexity with confidence. As your experienced sparring partner, I bring innovation, strategic insight, and proven methodologies to drive measurable business value across Automotive, IT, SAP, Manufacturing, Software, AI, Development, Business Models, Building, Telco, Public Sector, and Startup industries.",
      cta: "Request a Quote",
      trustBadge: "World-wide Experience Network"
    },
    de: {
      headline: "Strategische Führung für die Digitale Transformation",
      subheadline: "Unabhängige Executive Beratung für CIOs, CTOs & CEOs",
      description: "Navigieren Sie Komplexität mit Zuversicht. Als Ihr erfahrener Sparringspartner bringe ich Innovation, strategische Einsichten und bewährte Methoden, um messbaren Geschäftswert in den Branchen Automotive, IT, SAP, Fertigung, Software, KI, Entwicklung, Geschäftsmodelle, Bau, Telekommunikation, Öffentlicher Sektor und Startups zu schaffen.",
      cta: "Angebot anfragen",
      trustBadge: "Weltweites Erfahrungsnetzwerk"
    }
  };

  const t = content[language];

  return (
    <section id="executive-consulting" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src={heroImage} 
          alt="Executive consulting and strategic innovation"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/85 to-primary/70" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-4xl">
          {/* Trust Badge */}
          <div className="inline-flex items-center gap-2 bg-accent/10 backdrop-blur-sm border border-accent/20 rounded-full px-4 py-2 mb-6 shadow-glow">
            <Globe className="h-4 w-4 text-accent" />
            <span className="text-sm font-medium text-primary-foreground">{t.trustBadge}</span>
          </div>

          {/* Headline */}
          <h1 className="text-primary-foreground mb-4 leading-tight">
            {t.headline}
          </h1>
          
          <p className="text-2xl md:text-3xl text-accent font-semibold mb-6">
            {t.subheadline}
          </p>

          <p className="text-lg md:text-xl text-primary-foreground/90 mb-8 leading-relaxed max-w-3xl">
            {t.description}
          </p>

          {/* CTA Button */}
          <Button 
            variant="accent" 
            size="xl"
            onClick={onRequestQuote}
            className="group"
          >
            {t.cta}
            <ArrowRight className="h-5 w-5 transition-bounce group-hover:translate-x-1" />
          </Button>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
        <div className="w-6 h-10 border-2 border-primary-foreground/30 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-accent rounded-full" />
        </div>
      </div>
    </section>
  );
};